import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trash2, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { api, setToken, getToken, formatApiError } from "@/lib/api";
import { EventsPanel } from "@/components/admin/EventsPanel";

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      setToken(data.access_token);
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#FAF7F2] flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="flex items-center gap-3 mb-10">
          <img src="/images/logo.png" alt="Pause." className="h-12 w-12 rounded-full" />
          <span className="font-display text-3xl">Pause<span className="text-[#F5C800]">.</span></span>
        </div>
        <h1 className="font-display font-light text-4xl mb-2">Welcome back.</h1>
        <p className="font-body text-[#FAF7F2]/50 mb-8 text-sm">Sign in to view your waitlist.</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-6">
          <div>
            <label className="font-body text-xs uppercase tracking-[0.16em] text-[#FAF7F2]/50">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-transparent border-b-2 border-[#FAF7F2]/30 py-2 font-body text-lg focus:outline-none focus:border-[#F5C800] transition-colors duration-300"
              placeholder="nisha@pause.com"
            />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.16em] text-[#FAF7F2]/50">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-transparent border-b-2 border-[#FAF7F2]/30 py-2 font-body text-lg focus:outline-none focus:border-[#F5C800] transition-colors duration-300"
              placeholder="••••••••"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="font-body text-sm text-[#F5C800]">{error}</p>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full font-body text-sm uppercase tracking-[0.14em] font-medium bg-[#F5C800] text-[#1A1A1A] py-3.5 rounded-full hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <a href="/" className="mt-8 inline-block font-body text-xs uppercase tracking-[0.14em] text-[#FAF7F2]/40 hover:text-[#FAF7F2]/80 transition-colors">
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("waitlist");
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, this_week: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([api.get("/admin/waitlist"), api.get("/admin/stats")]);
      setEntries(list.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load waitlist");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setStats((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = entries.filter((e) => {
    const q = query.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.email.toLowerCase().includes(q) ||
      (e.phone || "").toLowerCase().includes(q) ||
      (e.location || "").toLowerCase().includes(q) ||
      (e.interest || "").toLowerCase().includes(q) ||
      (e.message || "").toLowerCase().includes(q)
    );
  });

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]">
      <header className="border-b border-[#8C8070]/20 bg-[#FAF7F2]/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="Pause." className="h-10 w-10 object-contain" />
            <span className="font-display text-2xl">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-body text-sm text-[#8C8070]">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="font-body text-sm inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1A1A1A]/20 hover:bg-[#1A1A1A] hover:text-[#FAF7F2] transition-colors duration-300"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-5 md:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#8C8070]/20">
          {[
            { key: "waitlist", label: "Waitlist" },
            { key: "events", label: "Events" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`font-body text-sm uppercase tracking-[0.14em] px-4 py-3 -mb-px border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#1A1A1A] text-[#1A1A1A]"
                  : "border-transparent text-[#8C8070] hover:text-[#1A1A1A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "events" ? (
          <EventsPanel />
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 gap-5 mb-10 max-w-md">
          <div data-testid="stat-total" className="rounded-sm border border-[#8C8070]/25 p-6">
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070]">Total on list</p>
            <p className="font-display text-5xl mt-2">{stats.total}</p>
          </div>
          <div data-testid="stat-week" className="rounded-sm border border-[#8C8070]/25 p-6">
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070]">This week</p>
            <p className="font-display text-5xl mt-2 text-[#3B5A3A]">{stats.this_week}</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 mb-6 max-w-md border-b-2 border-[#1A1A1A]/20 focus-within:border-[#3B5A3A] transition-colors">
          <Search className="h-4 w-4 text-[#8C8070]" />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, city, notes"
            className="w-full bg-transparent py-2 font-body focus:outline-none"
          />
        </div>

        {/* Entries */}
        {loading ? (
          <p className="font-body text-[#8C8070] py-16">Loading…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-empty" className="font-body text-[#8C8070] py-16">
            {entries.length === 0 ? "No sign-ups yet. Share the site to start collecting." : "No matches."}
          </p>
        ) : (
          <>
            {/* Mobile — cards */}
            <div className="md:hidden space-y-4" data-testid="waitlist-cards">
              {filtered.map((e) => (
                <div
                  key={e.id}
                  data-testid={`waitlist-card-${e.id}`}
                  className="rounded-sm border border-[#8C8070]/25 p-5 bg-[#FAF7F2]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-body font-medium text-lg">{e.name}</p>
                      <a href={`mailto:${e.email}`} className="font-body text-sm text-[#1A1A1A]/75 break-all hover:text-[#3B5A3A] transition-colors">
                        {e.email}
                      </a>
                    </div>
                    <button
                      data-testid={`delete-${e.id}`}
                      onClick={() => remove(e.id)}
                      className="text-[#8C8070] hover:text-[#b3261e] transition-colors shrink-0"
                      aria-label={`Remove ${e.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <dl className="mt-4 grid grid-cols-[90px_1fr] gap-y-2 gap-x-3 font-body text-sm">
                    <dt className="text-[10px] uppercase tracking-[0.16em] text-[#8C8070] pt-0.5">Phone</dt>
                    <dd className="text-[#1A1A1A]/80">
                      {e.phone ? (
                        <a href={`tel:${e.phone}`} className="hover:text-[#3B5A3A] transition-colors">{e.phone}</a>
                      ) : (
                        <span className="text-[#8C8070]">—</span>
                      )}
                    </dd>

                    <dt className="text-[10px] uppercase tracking-[0.16em] text-[#8C8070] pt-0.5">Location</dt>
                    <dd className="text-[#1A1A1A]/80">{e.location || <span className="text-[#8C8070]">—</span>}</dd>

                    <dt className="text-[10px] uppercase tracking-[0.16em] text-[#8C8070] pt-0.5">Interest</dt>
                    <dd className="text-[#1A1A1A]/80">{e.interest || <span className="text-[#8C8070]">—</span>}</dd>

                    <dt className="text-[10px] uppercase tracking-[0.16em] text-[#8C8070] pt-0.5">Joined</dt>
                    <dd className="text-[#8C8070]">{fmt(e.created_at)}</dd>
                  </dl>

                  {e.message && (
                    <div className="mt-4 pt-4 border-t border-[#8C8070]/20">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8C8070] mb-1.5">Note</p>
                      <p className="font-body text-sm text-[#1A1A1A]/85 leading-relaxed whitespace-pre-wrap">{e.message}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop — table */}
            <div className="hidden md:block overflow-x-auto rounded-sm border border-[#8C8070]/25">
              <table className="w-full text-left" data-testid="waitlist-table">
                <thead>
                  <tr className="border-b border-[#8C8070]/20 font-body text-xs uppercase tracking-[0.14em] text-[#8C8070]">
                    <th className="py-4 px-5">Name</th>
                    <th className="py-4 px-5">Email</th>
                    <th className="py-4 px-5">Phone</th>
                    <th className="py-4 px-5 hidden lg:table-cell">Location</th>
                    <th className="py-4 px-5 hidden lg:table-cell">Interest</th>
                    <th className="py-4 px-5">Note</th>
                    <th className="py-4 px-5 hidden xl:table-cell">Joined</th>
                    <th className="py-4 px-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.id} data-testid={`waitlist-row-${e.id}`} className="border-b border-[#8C8070]/12 last:border-0 hover:bg-[#F2EDE5]/60 transition-colors align-top">
                      <td className="py-4 px-5 font-body font-medium">{e.name}</td>
                      <td className="py-4 px-5 font-body text-[#1A1A1A]/75 break-all">{e.email}</td>
                      <td className="py-4 px-5 font-body text-[#1A1A1A]/70">
                        {e.phone ? (
                          <a href={`tel:${e.phone}`} className="hover:text-[#3B5A3A] transition-colors">{e.phone}</a>
                        ) : (
                          <span className="text-[#8C8070]">—</span>
                        )}
                      </td>
                      <td className="py-4 px-5 font-body text-[#1A1A1A]/70 hidden lg:table-cell">{e.location || <span className="text-[#8C8070]">—</span>}</td>
                      <td className="py-4 px-5 font-body text-[#1A1A1A]/60 hidden lg:table-cell">{e.interest || <span className="text-[#8C8070]">—</span>}</td>
                      <td className="py-4 px-5 font-body text-[#1A1A1A]/70 max-w-[260px]">
                        {e.message ? (
                          <span className="line-clamp-3 whitespace-pre-wrap" title={e.message}>{e.message}</span>
                        ) : (
                          <span className="text-[#8C8070]">—</span>
                        )}
                      </td>
                      <td className="py-4 px-5 font-body text-[#8C8070] hidden xl:table-cell">{fmt(e.created_at)}</td>
                      <td className="py-4 px-5 text-right">
                        <button
                          data-testid={`delete-desktop-${e.id}`}
                          onClick={() => remove(e.id)}
                          className="text-[#8C8070] hover:text-[#b3261e] transition-colors"
                          aria-label={`Remove ${e.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setToken(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  if (checking) {
    return <div className="min-h-screen bg-[#1A1A1A] text-[#FAF7F2] flex items-center justify-center font-body">…</div>;
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onSuccess={setUser} />;
}
